import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { BudgetServiceOrdersService } from '../../../services/budget-service-orders.service';
import { ServiceOrderWorkSectionsService } from '../../../services/service-order-work-sections.service';
import { ServiceOrdersService } from '../../../services/service-orders.service';
import { toBrazilWallClockString } from '../../../utils/datetime.utils';

export interface BudgetLineInput {
  description: string;
  quantity: number;
  unitValue: number;
  /** Preventiva | Corretiva. */
  maintenanceKind: string;
  /** Percentual cobrado a mais na corretiva (null/0 na preventiva). */
  chargePercent: number | null;
  /** Linha de guincho com fornecedor próprio no mesmo orçamento. */
  isGuincho?: boolean;
  externalServiceProviderId?: number | null;
  externalServiceProviderName?: string | null;
}

export interface BudgetCreateFlowInput {
  b2bContractId: number;
  plate: string;
  vehicleId: number | null;
  configOrderId: number;
  departmentId: number;
  openingReason: string | null;
  /** Obrigatório apenas para tipos de OS terceirizados. */
  providerId?: number | null;
  providerName?: string | null;
  services: BudgetLineInput[];
  products: BudgetLineInput[];
  /** Data/hora em que o orçamento foi gerado (retroativa). */
  recordedAt?: string | null;
  /** Quando veio de um agendamento, marca a OS como agendada. */
  isScheduled?: boolean;
  /** Indica se o orçamento é executado na base (sem prestador externo). */
  isBaseExecution?: boolean;
}

export interface BudgetCreateFlowResult {
  budget: any;
  serviceOrderId: number;
  osNumber: string;
}

/** Linhas do formulário no formato aceito pelo endpoint transacional. */
function toTransactionalLines(lines: BudgetLineInput[]) {
  return (lines || []).map((line) => ({
    description: line.description,
    quantity: line.quantity,
    unitValue: line.unitValue,
    maintenanceKind: line.maintenanceKind,
    chargePercent: line.chargePercent,
    isGuincho: !!line.isGuincho,
    externalServiceProviderId: line.isGuincho ? (line.externalServiceProviderId ?? null) : null
  }));
}

@Injectable({ providedIn: 'root' })
export class BudgetServiceOrderCreateFlowService {
  constructor(
    private serviceOrdersService: ServiceOrdersService,
    private workSectionsService: ServiceOrderWorkSectionsService,
    private budgetService: BudgetServiceOrdersService
  ) {}

  /**
   * Criação a partir de um agendamento: uma chamada só, transacional no backend,
   * já vinculando a OS ao agendamento.
   */
  createFromAppointment(
    appointmentId: number,
    input: BudgetCreateFlowInput
  ): Observable<BudgetCreateFlowResult> {
    return this.serviceOrdersService.createBudgetFromAppointment(appointmentId, {
      configOrderId: input.configOrderId,
      departmentId: input.departmentId,
      providerId: input.isBaseExecution ? null : (input.providerId ?? null),
      providerName: input.isBaseExecution ? null : (input.providerName ?? null),
      openingReason: input.openingReason,
      vehicleId: input.vehicleId,
      b2bContractId: input.b2bContractId,
      plate: input.plate,
      services: toTransactionalLines(input.services),
      products: toTransactionalLines(input.products),
      recordedAt: input.recordedAt ?? null
    }).pipe(
      map((result) => ({
        budget: { id: result.budgetId, status: result.status },
        serviceOrderId: result.serviceOrderId,
        osNumber: result.osNumber
      }))
    );
  }

  /**
   * Criação avulsa (sem agendamento). Mantém a orquestração no front porque o
   * endpoint transacional é ancorado em um agendamento.
   */
  create(input: BudgetCreateFlowInput) {
    const isBase = !!input.isBaseExecution;
    const providerName = isBase ? 'Base' : (input.providerName || 'Parceiro');
    const descriptions = [...input.services, ...input.products]
      .map((item) => item.description)
      .filter(Boolean)
      .join(', ');

    return this.serviceOrdersService.create({
      openingDate: input.recordedAt || toBrazilWallClockString(new Date()),
      description: `Orçamento B2B | ${isBase ? 'Base' : `Oficina: ${providerName}`} | ${descriptions}`,
      openingReason: input.openingReason,
      configOrderId: input.configOrderId,
      departmentId: input.departmentId,
      DepartmentId: input.departmentId,
      vehicleId: input.vehicleId,
      // Veículo de contrato B2B não tem vehicleId: o backend resolve pela placa.
      plate: input.plate,
      status: 'Em Orçamento',
      b2bContractId: input.b2bContractId,
      isScheduled: !!input.isScheduled
    } as any).pipe(
      switchMap((os) => {
        const serviceOrderId = Number(os?.id || 0);
        if (!serviceOrderId) throw new Error('OS criada sem identificador.');

        return this.workSectionsService.create({
          serviceOrderId,
          description: isBase ? 'Orçamento — Base' : `Orçamento — ${providerName}`,
          status: 'Pendente',
          externalServiceProviderId: isBase ? null : (input.providerId ?? null)
        }, false).pipe(
          map((section) => ({
            serviceOrderId,
            osNumber: String(os?.numeroOS || '').trim(),
            section
          }))
        );
      }),
      switchMap(({ serviceOrderId, osNumber, section }) => {
        const workSectionId = Number(section?.id || 0);
        if (!workSectionId) throw new Error('Seção de trabalho criada sem identificador.');

        const serviceRequests = input.services.map((service) =>
          this.workSectionsService.createService({
            serviceOrderWorkSectionId: workSectionId,
            serviceTypeId: null,
            externalServiceProviderId: service.isGuincho
              ? (service.externalServiceProviderId ?? null)
              : (isBase ? null : (input.providerId ?? null)),
            description: service.isGuincho && service.externalServiceProviderName
              ? (service.description?.startsWith('Guincho')
                ? service.description
                : `Guincho — ${service.externalServiceProviderName}`)
              : service.description,
            quantity: service.quantity,
            unitValue: service.unitValue,
            maintenanceKind: service.maintenanceKind,
            chargePercent: service.chargePercent,
            isGuincho: !!service.isGuincho,
            paymentStatus: 'Pendente',
            // Corretiva cobrada = avaria na OS; preventiva permanece sem cobrança.
            hasFault: this.isChargeableFault(service)
          }, false)
        );
        const productRequests = input.products.map((product) =>
          this.workSectionsService.createItem({
            serviceOrderWorkSectionId: workSectionId,
            itemId: null,
            ...product,
            paymentStatus: 'Pendente',
            hasFault: this.isChargeableFault(product),
            withdrawnFromStock: false,
            inStock: false
          }, false)
        );

        const requests = [...serviceRequests, ...productRequests];
        return (requests.length ? forkJoin(requests) : of([])).pipe(
          map(() => ({ serviceOrderId, osNumber, workSectionId }))
        );
      }),
      switchMap(({ serviceOrderId, osNumber, workSectionId }) =>
        this.budgetService.create({
          serviceOrderWorkSectionId: workSectionId,
          osNumber: osNumber || undefined,
          status: 'Pendente',
          clientApproved: false,
          plateVechile: input.plate || undefined
        }).pipe(
          map((budget) => ({
            budget,
            serviceOrderId,
            osNumber
          } as BudgetCreateFlowResult))
        )
      )
    );
  }

  /** Corretiva com valor > 0 é cobrada como avaria; preventiva fica zerada. */
  private isChargeableFault(line: BudgetLineInput): boolean {
    const kind = String(line.maintenanceKind || '').toLowerCase();
    if (kind.startsWith('prevent')) return false;

    const qty = Number(line.quantity || 0);
    const unit = Number(line.unitValue || 0);
    const percent = Number(line.chargePercent || 0);
    const total = Math.round(qty * unit * (1 + (percent > 0 ? percent / 100 : 0)) * 100) / 100;
    return total > 0;
  }
}
